import React from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ScatterChart, Scatter } from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { MapPin, AlertTriangle, TrendingUp } from 'lucide-react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

interface TimeRange {
  from: Date | null
  to: Date | null
  preset?: string
}

interface GeographicAnalysisProps {
  timeRange: TimeRange
}

const zipCodeData = [
  { zip: '80112', count: 432, percentage: 32.1, risk: 'critical', trend: 'increasing', state: 'CO', city: 'Englewood' },
  { zip: '90210', count: 89, percentage: 6.6, risk: 'medium', trend: 'stable', state: 'CA', city: 'Beverly Hills' },
  { zip: '10001', count: 67, percentage: 5.0, risk: 'medium', trend: 'decreasing', state: 'NY', city: 'New York' },
  { zip: '60601', count: 45, percentage: 3.4, risk: 'low', trend: 'stable', state: 'IL', city: 'Chicago' },
  { zip: '75201', count: 38, percentage: 2.8, risk: 'low', trend: 'stable', state: 'TX', city: 'Dallas' },
  { zip: '33101', count: 34, percentage: 2.5, risk: 'medium', trend: 'increasing', state: 'FL', city: 'Miami' },
  { zip: '94102', count: 31, percentage: 2.3, risk: 'low', trend: 'decreasing', state: 'CA', city: 'San Francisco' },
  { zip: '02101', count: 28, percentage: 2.1, risk: 'low', trend: 'stable', state: 'MA', city: 'Boston' }
]

const stateData = [
  { state: 'Colorado', count: 567, percentage: 42.1, risk: 'critical' },
  { state: 'California', count: 234, percentage: 17.4, risk: 'medium' },
  { state: 'New York', count: 156, percentage: 11.6, risk: 'medium' },
  { state: 'Texas', count: 123, percentage: 9.1, risk: 'low' },
  { state: 'Florida', count: 89, percentage: 6.6, risk: 'low' },
  { state: 'Illinois', count: 67, percentage: 5.0, risk: 'low' }
]

const getRiskColor = (risk: string) => {
  switch (risk) {
    case 'critical': return '#EF4444'
    case 'medium': return '#F59E0B'
    case 'low': return '#10B981'
    default: return '#6B7280'
  }
}

const getTrendIcon = (trend: string) => {
  switch (trend) {
    case 'increasing': return <TrendingUp className="h-3 w-3 text-red-500" />
    case 'decreasing': return <TrendingUp className="h-3 w-3 text-green-500 rotate-180" />
    default: return <div className="h-3 w-3 bg-gray-400 rounded-full" />
  }
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    return (
      <div className="bg-card border rounded-lg shadow-lg p-3">
        <p className="font-semibold text-foreground">{data.zip || data.state}</p>
        {data.city && <p className="text-sm text-muted-foreground">{data.city}, {data.state}</p>}
        <p className="text-sm">Count: {data.count}</p>
        <p className="text-sm">Percentage: {data.percentage}%</p>
        <p className="text-sm capitalize">Risk: {data.risk}</p>
      </div>
    )
  }
  return null
}

export const GeographicAnalysis: React.FC<GeographicAnalysisProps> = ({ timeRange }) => {
  return (
    <div className="space-y-6">
      {/* Critical Alert */}
      <Card className="border-red-200 dark:border-red-800">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <div>
              <p className="font-semibold text-red-900 dark:text-red-100">
                High Geographic Concentration Alert
              </p>
              <p className="text-sm text-red-700 dark:text-red-300">
                Zip code 80112 represents 32.1% of all fraudulent submissions. This exceeds our 5% threshold by 6x.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Zip Code Concentration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Zip Code Concentration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={zipCodeData.slice(0, 6)} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="zip" type="category" width={50} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="count" 
                    fill="#EF4444"
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* State Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>State Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stateData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="state" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="count" 
                    fill="#3B82F6"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Zip Code Table */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Geographic Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Zip Code</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Count</TableHead>
                <TableHead>% of Total</TableHead>
                <TableHead>Risk Level</TableHead>
                <TableHead>Trend</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {zipCodeData.map((zip, index) => (
                <TableRow key={index}>
                  <TableCell className="font-mono">{zip.zip}</TableCell>
                  <TableCell>{zip.city}, {zip.state}</TableCell>
                  <TableCell className="font-semibold">{zip.count}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span>{zip.percentage}%</span>
                      <div 
                        className="h-2 w-16 bg-gray-200 rounded-full overflow-hidden"
                      >
                        <div 
                          className="h-full rounded-full transition-all duration-300"
                          style={{ 
                            width: `${Math.min(zip.percentage * 3, 100)}%`,
                            backgroundColor: getRiskColor(zip.risk)
                          }}
                        />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant={zip.risk === 'critical' ? 'destructive' : 
                               zip.risk === 'medium' ? 'default' : 'secondary'}
                      className="capitalize"
                    >
                      {zip.risk}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getTrendIcon(zip.trend)}
                      <span className="text-sm capitalize">{zip.trend}</span>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Geographic Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">80112</p>
            <p className="text-sm text-muted-foreground">Highest Risk Zip</p>
            <p className="text-xs text-red-600 mt-1">32.1% of all fraud</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-orange-600">6</p>
            <p className="text-sm text-muted-foreground">States &gt; 5% Threshold</p>
            <p className="text-xs text-orange-600 mt-1">Need monitoring</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">42%</p>
            <p className="text-sm text-muted-foreground">CO Concentration</p>
            <p className="text-xs text-green-600 mt-1">Primary focus area</p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}